import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Card, Button, Badge } from '../components/ui';

interface License {
  id: string;
  license_key: string;
  ip: string;
  port: number;
  status: string;
  expires_at: string | null;
  product_id?: string;
}

interface OrderWithItems {
  id: string;
  status: string;
  total_price: number;
  created_at: string;
  items: { product_id: string; price: number; quantity: number }[];
}

export default function DashboardCliente() {
  const [licenses, setLicenses] = useState<License[]>([]);
  const [orders, setOrders] = useState<OrderWithItems[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get('/admin/licenses').then(r => setLicenses(r.data)).catch(() => setLicenses([])),
      api.get('/orders/my?limit=5&page=1').then(r => {
        setOrders(r.data?.data ?? []);
        setOrdersTotal(r.data?.total ?? 0);
      }).catch(() => { setOrders([]); setOrdersTotal(0); }),
    ]).finally(() => setLoading(false));
  }, []);

  const activeLicenses = licenses.filter((l: License) => l.status === 'active');

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <div className="animate-spin h-12 w-12 border-2 border-rs-accent border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <h1 className="font-display text-2xl font-bold text-gray-100 mb-8">Mi cuenta</h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        <Card padding="lg" className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">Licencias activas</p>
            <p className="text-3xl font-display font-bold text-rs-accent">{activeLicenses.length}</p>
          </div>
          <Link to="/licenses">
            <Button variant="outline" size="sm">Ver mis licencias</Button>
          </Link>
        </Card>
        <Card padding="lg" className="flex items-center justify-between">
          <div>
            <p className="text-gray-500 text-sm">Compras realizadas</p>
            <p className="text-3xl font-display font-bold text-rs-accent">{ordersTotal}</p>
          </div>
          <Link to="/orders">
            <Button variant="outline" size="sm">Ver mis compras</Button>
          </Link>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="font-display text-lg font-semibold text-gray-100 mb-4">Últimas licencias</h2>
          {licenses.length === 0 ? (
            <p className="text-gray-500">No tienes licencias aún.</p>
          ) : (
            <div className="space-y-2">
              {licenses.slice(0, 5).map((l: License) => (
                <div key={l.id} className="flex justify-between items-center py-2 border-b border-rs-border last:border-0">
                  <span className="text-gray-300 font-mono text-sm truncate max-w-[200px]">{l.license_key}</span>
                  <Badge variant={l.status === 'active' ? 'success' : l.status === 'suspended' ? 'warning' : 'default'}>
                    {l.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          <Link to="/licenses" className="inline-block mt-4 text-rs-accent hover:underline text-sm">Ver todas →</Link>
        </Card>

        <Card>
          <h2 className="font-display text-lg font-semibold text-gray-100 mb-4">Últimas compras</h2>
          {orders.length === 0 ? (
            <p className="text-gray-500">No tienes compras aún.</p>
          ) : (
            <div className="space-y-2">
              {orders.slice(0, 5).map((o: OrderWithItems) => (
                <div key={o.id} className="flex justify-between items-center py-2 border-b border-rs-border last:border-0">
                  <span className="text-gray-300 text-sm">${Number(o.total_price).toFixed(2)}</span>
                  <Badge variant={o.status === 'paid' ? 'success' : o.status === 'pending' ? 'warning' : 'default'}>
                    {o.status}
                  </Badge>
                </div>
              ))}
            </div>
          )}
          <Link to="/orders" className="inline-block mt-4 text-rs-accent hover:underline text-sm">Ver todas →</Link>
        </Card>
      </div>

      <div className="mt-8">
        <Link to="/">
          <Button variant="secondary">Ir al catálogo</Button>
        </Link>
      </div>
    </div>
  );
}
